// Objective: Define the service to get the loan leads
import HttpClient from "../http/HttpClient";
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from "../constants/RestConstants";
import { PaginationConst } from '../constants/AppConstant';

export const getPayments = async (leadId: string, pageNumber: number = 0, pageSize: number = PaginationConst.PAGE_SIZE, filter: string) => {
  try {
    const response = await HttpClient.GET({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/${leadId}/payments?${filter}`
    });
    return response;
  } catch (error) {
    console.error("Error fetching payments:", error);
    throw error;
  }
};

export const createdPayment = async (debitId: string, releaseObj: Object = {}) => {
  try {
    const response = await HttpClient.POST({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/payments`,
      data: releaseObj,
    });
    return response;
  } catch (error) {
    console.error("Error creating payment:", error);
    throw error;
  }
};

export const getPaymentsByDebit = async (debitId: string, pageNumber: number = 0, pageSize: number = PaginationConst.PAGE_SIZE) => {
  try {
    const response = await HttpClient.GET({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/payments?pageNumber=${pageNumber}&pageSize=${pageSize}`
    });
    return response;
  } catch (error) {
    console.error("Error fetching payments by debit:", error);
    throw error;
  }
};

export const createPayment = async (debitId: string, paymentData: any) => {
  try {
    const response = await HttpClient.POST({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/debits/${debitId}/payments`,
      data: paymentData,
    });
    return response;
  } catch (error) {
    console.error("Error creating payment:", error);
    throw error;
  }
};

export const getPaymentById = async (paymentId: string) => {
  try {
    const response = await HttpClient.GET({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/payments/${paymentId}`
    });
    return response;
  } catch (error) {
    console.error("Error fetching payment by ID:", error);
    throw error;
  }
};

export const updatePayment = async (paymentId: string, paymentData: any) => {
  try {
    const response = await HttpClient.PUT({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/payments/${paymentId}`,
      data: paymentData,
    });
    return response;
  } catch (error) {
    console.error("Error updating payment:", error);
    throw error;
  }
};

export const deletePayment = async (paymentId: string) => {
  try {
    const response = await HttpClient.DELETE({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/payments/${paymentId}`
    });
    return response;
  } catch (error) {
    console.error("Error deleting payment:", error);
    throw error;
  }
};

export const getAllPayments = async (pageNumber: number = 0, pageSize: number = PaginationConst.PAGE_SIZE, filters?: Record<string, any>) => {
  try {
    // Build query parameters
    const params: any = {
      page: pageNumber,
      size: pageSize,
    };
    
    // Add filter parameters if provided
    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value;
        }
      });
    }
    
    const queryString = new URLSearchParams(params).toString();
    const response = await HttpClient.GET({
      BASE_URL: LEAD_SERVICE_HOST,
      END_POINT: `${LEAD_API_PREFIX}/payments?${queryString}`
    });
    return response.data || response;
  } catch (error) {
    console.error("Error fetching all payments:", error);
    throw error;
  }
};

/** Admin: payments for one borrower (paginated). */
export const getPaymentsByBorrower = async (
  borrowerId: string,
  pageNumber: number = 0,
  pageSize: number = PaginationConst.PAGE_SIZE
) => {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/borrowers/${encodeURIComponent(borrowerId)}/payments?page=${pageNumber}&size=${pageSize}`,
  });
  return response.data || response;
};

/** Load up to maxRows of a borrower's payments for charts (newest first from API). */
export async function getPaymentsByBorrowerBulk(
  borrowerId: string,
  maxRows: number = 500
): Promise<{ result: any[]; totalCount: number }> {
  const pageSize = Math.min(maxRows, 200);
  const first = await getPaymentsByBorrower(borrowerId, 0, pageSize);
  const result = first?.result ?? [];
  const totalCount = Number(first?.totalCount ?? result.length) || 0;
  if (totalCount <= pageSize || result.length >= maxRows) {
    return { result, totalCount };
  }
  const pagesNeeded = Math.min(Math.ceil(Math.min(totalCount, maxRows) / pageSize), 5);
  const rest: any[] = [];
  for (let page = 1; page < pagesNeeded; page++) {
    const res = await getPaymentsByBorrower(borrowerId, page, pageSize);
    rest.push(...(res?.result ?? []));
  }
  return { result: [...result, ...rest].slice(0, maxRows), totalCount };
}